import { AlertTriangle } from "lucide-react";
import type { EvaluationOutcome } from "@/types";
import { notEvaluableLabel } from "@/utils/evaluation";
import { formatScore, incomparableReason, incomparableTag, isComparable } from "@/utils/rubric";
import { cn } from "@/lib/utils";

/**
 * The C5 renderer. Owned by Test Cases, consumed by Dashboard and every screen
 * in this tab (TECH_SPEC_FE §3).
 *
 * **Form, not colour position.** The four states are told apart by four
 * independent channels, so they survive greyscale, colour-vision deficiency
 * and 11px type:
 *
 *   채점됨    a number is there
 *   평가 불가  a dashed outline with a dash where the number would be
 *   실패      an icon is there
 *   미실행    a bare dash, no outline
 *
 * No success colour on 채점됨. A high score is not a success and a low one is
 * not a failure — the pass/fail axis is orthogonal to the score axis, and
 * painting 채점됨 green puts 평가 불가 implicitly below it on a scale. That is
 * the misreading C5 exists to prevent.
 */
export function EvaluationStatusBadge({
  outcome,
  size = "default",
  className,
}: {
  outcome: EvaluationOutcome;
  size?: "default" | "compact";
  className?: string;
}) {
  const base = cn(
    "inline-flex items-center gap-1.5 rounded-lg border",
    size === "compact" ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-1 text-xs",
    className,
  );

  switch (outcome.status) {
    case "scored":
      // A v1 score is still a score — but on another scale, so it says so
      // rather than sitting beside 0–1 numbers as if it were one of them.
      return isComparable(outcome) ? (
        <span
          className={cn(base, "border-border bg-surface-input font-medium text-foreground")}
          title="채점됨"
        >
          <span className="tabular-nums">{formatScore(outcome.score)}</span>
        </span>
      ) : (
        <span
          className={cn(base, "border-border bg-transparent text-muted-foreground")}
          title={incomparableReason(outcome)}
        >
          <span className="tabular-nums">{formatScore(outcome.score)}</span>
          <span className="text-[10px]">{incomparableTag(outcome)}</span>
        </span>
      );

    case "not_evaluable":
      return (
        <span
          className={cn(base, "border-dashed border-border bg-transparent text-muted-foreground")}
          title={`평가 불가 — ${notEvaluableLabel(outcome.reason)}`}
        >
          <span aria-hidden>—</span>
          <span>평가 불가</span>
        </span>
      );

    case "failed":
      return (
        <span
          className={cn(
            base,
            "border-destructive-line bg-destructive-surface text-destructive [&>svg]:size-3.5",
          )}
          title={outcome.reason ? `실패 — ${outcome.reason}` : "실패"}
        >
          <AlertTriangle />
          <span>실패</span>
        </span>
      );

    case "not_run":
      return (
        <span
          className={cn(base, "border-transparent bg-transparent text-muted-foreground")}
          title="아직 실행되지 않았습니다"
        >
          <span aria-hidden>—</span>
          <span className="sr-only">미실행</span>
        </span>
      );
  }
}
